import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { findAllFamily, findOneFamily, syncFamily } from "./api";
import { ADDITION_DELETION_LABEL, SPOUSE_CHILDREN_INSTRUCTIONS } from "./constants";
import { attachToRemarks, employeeProfile, formatApprover, getSessionUser, remarksFromToRemarks } from "./helpers";
import { CreateBar, EmptyState, InstructionModal, Line, PageShell, RequestCard, StatusBadge } from "./ui";

type Row = {
  referenceNumber: string;
  creationDate: string;
  personalNo: string;
  action: string;
  dependentName: string;
  status: string;
  approver: string;
  remarks: string;
};

const mapItem = (item: any): Row => {
  const firstName = item?.RelativeFirstName ?? item?.relativeFirstName ?? "";
  const lastName = item?.RelativeLastName ?? item?.relativeLastName ?? "";
  const code = String(item?.FamilyMembersAdditionDeletion ?? item?.familyMembersAdditionDeletion ?? "").trim();
  const action = ADDITION_DELETION_LABEL[code] ?? String(item?.Action ?? item?.action ?? "");
  return {
    referenceNumber: String(item?.ReferenceNumber ?? item?.referenceNumber ?? ""),
    creationDate: item?.RequestCreationDate ?? "-",
    personalNo: String(item?.EmpID ?? item?.empId ?? ""),
    action,
    dependentName: `${firstName} ${lastName}`.trim(),
    status: String(item?.RequestStatus ?? item?.Status ?? item?.status ?? ""),
    approver: formatApprover(
      item?.ApproverName ?? item?.approverName ?? item?.LatestApproverName,
      item?.ApproverUserID ?? item?.approverUserID
    ),
    remarks: remarksFromToRemarks(item),
  };
};

const FamilyMembersList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const fetchRequests = useCallback(async () => {
    const empId = employeeProfile(getSessionUser()).employeeNo;
    if (!empId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await findAllFamily(empId);
      if (res.ok) {
        const withRemarks = await attachToRemarks(res.list, findOneFamily);
        setRows(withRemarks.map(mapItem).reverse());
      }
      else toast.error(res.message || "Something went wrong");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSync = async () => {
    setLoading(true);
    const empId = employeeProfile(getSessionUser()).employeeNo;
    try {
      const res = await syncFamily(empId);
      if (res?.success) toast.success("Sync triggered successfully");
      else toast.error(res?.message || "Something went wrong");
    } catch {
      toast.error("Something went wrong");
    } finally {
      await fetchRequests();
    }
  };

  return (
    <PageShell
      title="Inclusion and Deletion"
      subtitle="List of Inclusion / Deletion of family members (Spouse and Children)"
      onSync={handleSync}
      syncing={loading}
    >
      <CreateBar label="Create New Request" onClick={() => setShowModal(true)} />
      {loading && <div className="text-center py-4">Loading...</div>}
      {!loading &&
        rows.map((item) => (
          <RequestCard
            key={item.referenceNumber}
            reference={item.referenceNumber}
            lines={
              <>
                <Line label="Request Creation Date" value={item.creationDate || "-"} />
                <Line label="Personal No." value={item.personalNo || "-"} />
                <Line label="Dependent Name" value={item.dependentName || "-"} />
                <Line label="Action" value={item.action || "-"} />
                <Line label="Last/Latest Approver" value={item.approver} />
                <Line label="Request Status" value={<StatusBadge status={item.status} />} />
                <Line wide label="Remarks" value={item.remarks || "-"} />
              </>
            }
          />
        ))}
      {!loading && rows.length === 0 && <EmptyState />}
      <InstructionModal
        open={showModal}
        text={SPOUSE_CHILDREN_INSTRUCTIONS}
        onClose={() => setShowModal(false)}
        onOk={() => {
          setShowModal(false);
          navigate(all_routes.idCard.familyMemberCreate);
        }}
      />
    </PageShell>
  );
};

export default FamilyMembersList;
